/* eslint-disable react-refresh/only-export-components */

import moment from "moment/moment";
import { BalanceTypeCategory, BalanceDirection } from "./constants/balances";

export const UNKNOWN_ERROR_MESSAGE =
  "Erreur inattendue. Rafraichissez la page et réessayez.";
export const VALIDATION_ERROR_MESSAGE =
  "Les données saisies sont incorrectes. Le serveur a renvoyé l'erreur suivante : ";

export function handleAPIError(error) {
  if (error.response) {
    const errorStr = JSON.stringify(error.response.data, null, 1);
    console.error(`Status ${error.response.status} : ${errorStr}`);
    if (error.response.status === 400) {
      if (typeof error.response.data?.error === "string") {
        return error.response.data.error;
      }
      return (
        <>
          {VALIDATION_ERROR_MESSAGE}
          <pre style={{ whiteSpace: "pre-wrap", textAlign: "left" }}>
            {errorStr}
          </pre>
        </>
      );
    } else if (error.response.status === 420) {
      return <>Paiement refusé</>;
    } else if (
      error.response.status === 402 ||
      error.response.status === 409 ||
      error.response.status === 410
    ) {
      return error.response.data?.error ?? UNKNOWN_ERROR_MESSAGE;
    }
    return UNKNOWN_ERROR_MESSAGE;
  } else {
    console.error(error);
    if (error?.displayMessage && error?.message) {
      return error.message;
    }
    return UNKNOWN_ERROR_MESSAGE;
  }
}

export function findAndUpdate(list, matcher, updatedItem) {
  const index = list.findIndex(matcher);
  if (index === -1) {
    throw new Error("Item non trouvé");
  }
  const newList = [...list];
  newList[index] = updatedItem;
  return newList;
}

export function findAndUpdateMultiple(list, matcher, updatedItems) {
  const newList = [...list];
  updatedItems.forEach((updatedItem) => {
    const index = newList.findIndex((item) => matcher(item, updatedItem));
    if (index !== -1) {
      newList[index] = updatedItem;
    }
  });
  return newList;
}

export function classnames(...classes) {
  return classes
    .filter((clazz) => typeof clazz === "string" && clazz !== "")
    .join(" ");
}

export function TextOneOrMany({ one, many, none, number }) {
  if (!number) return none ?? one;
  else if (number === 1) return one;
  else return many;
}

export function getNewLoyerFromIndice(montant, oldIndice, newIndice) {
  const newMontant = (montant * newIndice) / oldIndice;
  return +newMontant.toFixed(2);
}

export function getLastAnniversaryYear(date) {
  const today = moment();
  if (moment(date).year(today.year()) > today) {
    // Si la date d'anniversaire donnée, avec l'année d'aujd est après aujd,
    // le dernier anniversaire était l'année d'avant
    return today.year() - 1;
  } else {
    // Sinon l'anniversaire était cette année
    return today.year();
  }
}

export function getNextAnniversaryDate(date) {
  return moment(date).year(getLastAnniversaryYear(date) + 1);
}

export function getLastAnniversaryDate(date) {
  return moment(date).year(getLastAnniversaryYear(date));
}

export function flattenKeys(input, prefix = "", isArray = false) {
  if (typeof input !== "object") {
    return [prefix];
  }

  return Object.keys(input)
    .map((k) => {
      let suffix = `.${k}`;
      if (!prefix) suffix = `${k}`;
      else if (isArray) suffix = `[${k}]`;

      return flattenKeys(
        input[k],
        `${prefix}${suffix}`,
        Array.isArray(input[k]),
      );
    })
    .flat();
}

function mimetype(fileOrMimeType) {
  return typeof fileOrMimeType === "object"
    ? fileOrMimeType?.mimetype
    : fileOrMimeType;
}

export function isImage(fileOrMimeType) {
  return mimetype(fileOrMimeType)?.startsWith("image");
}

export function isPdf(fileOrMimeType) {
  return mimetype(fileOrMimeType) === "application/pdf";
}

export function showFormikFieldsInError(errors, setFieldTouched) {
  for (const key of flattenKeys(errors)) {
    setFieldTouched(key, true, false);
  }
}

function getStaticIdsByCategory(category, balanceTypesByCategory) {
  return (
    balanceTypesByCategory[category]?.map(
      (balanceType) => balanceType.static_id,
    ) || []
  );
}

export function isChargeOrTravauxType(type, balanceTypesByCategory) {
  return (
    getStaticIdsByCategory(
      BalanceTypeCategory.CHARGE,
      balanceTypesByCategory,
    ).includes(type) ||
    getStaticIdsByCategory(
      BalanceTypeCategory.TRAVAUX,
      balanceTypesByCategory,
    ).includes(type)
  );
}

function findBalanceType(type, balanceTypesByCategory) {
  return Object.values(balanceTypesByCategory)
    .flat()
    .find((balanceType) => balanceType.static_id === type);
}

export function setBalanceAmountSignFromType(
  balance,
  balanceTypesByCategory,
  isRemboursement = false,
) {
  const montant = parseFloat(balance.montant);
  // Le sens imposé par le catalogue prime sur la règle de la catégorie : un remboursement
  // à effectuer est un appel positif, un appel de fonds au bailleur un mouvement positif.
  const direction = findBalanceType(
    balance.type,
    balanceTypesByCategory,
  )?.direction;
  if (direction === BalanceDirection.CREDIT) {
    return Math.abs(montant);
  }
  if (direction === BalanceDirection.DEBIT) {
    return -Math.abs(montant);
  }
  const forcePositive =
    isChargeOrTravauxType(balance.type, balanceTypesByCategory) &&
    isRemboursement;
  const forceNegative =
    (isChargeOrTravauxType(balance.type, balanceTypesByCategory) &&
      !isRemboursement) ||
    getStaticIdsByCategory(
      BalanceTypeCategory.DG_CREDIT,
      balanceTypesByCategory,
    ).includes(balance.type) ||
    getStaticIdsByCategory(
      BalanceTypeCategory.LOCATAIRE_APPEL,
      balanceTypesByCategory,
    ).includes(balance.type) ||
    getStaticIdsByCategory(
      BalanceTypeCategory.PAIEMENT_BAILLEUR,
      balanceTypesByCategory,
    )?.includes(balance.type);

  if (forcePositive) {
    return Math.abs(montant);
  }
  if (forceNegative) {
    return -Math.abs(montant);
  }
  return montant;
}

export function stripEnd(str, char) {
  let endIndex = undefined;
  if (str.endsWith(char)) {
    endIndex = -1;
  }
  return str.slice(0, endIndex);
}

export function getUtmParams(searchParams) {
  const utm = {
    source: searchParams.get("utm_source") || undefined,
    medium: searchParams.get("utm_medium") || undefined,
    campaign: searchParams.get("utm_campaign") || undefined,
    term: searchParams.get("utm_term") || undefined,
    content: searchParams.get("utm_content") || undefined,
  };
  if (utm.length === 0) return undefined;
  return utm;
}

const PROMO_COOKIE = "qalimo_promo=";

/** Code promo d'un lien partenaire : paramètre `promo` de l'URL, sinon cookie posé sur
 * .qalimo.fr par le site vitrine quand le visiteur y est arrivé avec ce paramètre. */
export function getPromoCode(searchParams) {
  const fromUrl = searchParams.get("promo")?.trim();
  if (fromUrl) return fromUrl;
  const cookie = document.cookie
    .split("; ")
    .find((c) => c.startsWith(PROMO_COOKIE));
  return cookie
    ? decodeURIComponent(cookie.slice(PROMO_COOKIE.length)).trim() || undefined
    : undefined;
}

export function setUtmSearchParams(params, utm) {
  for (const [k, v] of Object.entries(utm)) {
    if (v) params.set(`utm_${k}`, v);
  }
}
